import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  Avatar,
  Chip,
  Stack,
  Divider,
  useTheme,
  alpha,
  List,
  ListItem,
  ListItemText,
  Button
} from '@mui/material';
import {
  School as SchoolIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Public as PublicIcon,
  Language as LanguageIcon,
  Timeline as TimelineIcon,
  Assignment as AssignmentIcon,
  Support as SupportIcon,
  Timeline as TimelineAltIcon,
  TrendingUp as TrendingUpIcon,
  Computer as ComputerIcon
} from '@mui/icons-material';
import { useSEO } from '../hooks/useSEO';
import { Link } from 'react-router-dom';
import useWeChatQR from '../hooks/useWeChatQR';
import WeChatQRDialog from '../components/common/WeChatQRDialog';

import heroImage from '../assets/images/main.jpg';

const BusinessCooperation = () => {
  const theme = useTheme();

  const { openQR, openWeChatQR, closeWeChatQR } = useWeChatQR();

  useSEO('business');

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
          py: { xs: 6, md: 10 }
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                fontWeight: 700,
                fontSize: { xs: '1.6rem', md: '2.5rem' },
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              与希航教育建立长期、稳定、可复制的项目合作
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 920, mx: 'auto', fontWeight: 500 }}>
              我们以To B视角提供留学与教育项目的方案设计、执行交付和境外落地支持，支持机构、学校及项目方的批量招生与项目化交付。
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* CTA strip like /services */}
      <Box sx={{ py: 4, backgroundColor: theme.palette.grey[50], borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              与我们合作，让项目可规模化落地
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
              预约对接会、定制化方案或索取合作手册，我们会在24小时内与您联系。
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button component={Link} to="/contact" variant="contained" size="large">预约对接会</Button>
              <Button variant="outlined" size="large" onClick={openWeChatQR}>索取合作手册</Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      <Container maxWidth={false} sx={{ py: 8, px: { xs: 2, md: 6 } }}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={12}>
            <Paper sx={{ p: 4, borderRadius: 3, mb: 4 }}>
              <Typography variant="h5" sx={{ color: '#052391', fontWeight: 700, mb: 2 }}>
                一、我们是谁
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, color: '#333', lineHeight: 1.8 }}>
                希航教育（XIHANG EDUCATION）是一家深耕马来西亚留学与国际教育合作10年+的专业教育服务机构，
                在中国 & 马来西亚均设有实体公司与执行团队，长期服务于：
              </Typography>
              <List>
                <ListItem>
                  <ListItemText primary="国内留学机构" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="高校 / 国际学校" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="教育集团" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="项目班 / 渠道方 / 投资方" />
                </ListItem>
              </List>

              <Typography variant="body1" sx={{ mt: 2, color: '#333', fontWeight: 600 }}>
                我们的核心定位是：
              </Typography>
              <Paper sx={{ p: 2, mt: 2, background: alpha(theme.palette.primary.main, 0.06) }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  “留学与教育项目的方案设计方 + 执行交付方 + 境外落地支持方”
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                  你负责资源、招生或渠道，我们负责把项目真正做成、做稳、交付到位。
                </Typography>
              </Paper>
            </Paper>

            <Paper sx={{ p: 3, borderRadius: 3, mb: 4 }}>
              <Typography variant="h5" sx={{ color: '#052391', fontWeight: 700, mb: 3, textAlign: 'center' }}>
                二、核心合作能力
              </Typography>

              {/* Card grid with icons */}
              <Grid container spacing={3}>
                {[
                  {
                    icon: <SchoolIcon />,
                    title: '留学申请项目合作（K12 / 本 / 硕 / 博）',
                    lines: [
                      '合作内容：马来西亚公立大学、私立大学、英澳名校马来西亚分校、国际学校（K12）',
                      '合作方式：官方授权招生合作、渠道对接、批量招生 / 项目制交付',
                    ]
                  },
                  {
                    icon: <CheckCircleIcon />,
                    title: '马来西亚前五公立大学｜定向保录取项目',
                    lines: [
                      '适用对象：项目班招生、成绩边缘学生、时间紧、结果导向型项目',
                      '项目优势：前置评估 + 风控方案、定向院校 / 专业、录取路径清晰、可控'
                    ]
                  },
                  {
                    icon: <PublicIcon />,
                    title: '签证与身份解决方案（高信任业务）',
                    lines: [
                      '服务范围：学生签证、签证加急（3–5 个工作日出文件）、第二家园（MM2H）、工作签证',
                      '合作优势：马来西亚本地团队直连、高通过率、支持 B 端批量处理'
                    ]
                  },
                  {
                    icon: <SupportIcon />,
                    title: '境外一站式交付服务（核心壁垒）',
                    lines: [
                      '接机、入学注册 / 选课、住宿安排、学籍与在读管理、家长沟通、境外应急处理',
                      '我们把学生真正送进校园、稳定在海外。'
                    ]
                  },
                  {
                    icon: <LanguageIcon />,
                    title: '语言与学术支持项目',
                    lines: [
                      '覆盖考试：MUET、托福、领思（Linguaskill）、校内语言测试',
                      '合作场景：项目班配套、升学衔接、批量学生语言解决方案'
                    ]
                  },
                  {
                    icon: <BusinessIcon />,
                    title: '中马高校合作 & 留学项目班共建',
                    lines: [
                      '合作内容：中马高校联合办学对接、专业共建 / 项目引进、学分互认 / 联合培养',
                      '项目班类型：高中→本科、专升本、本升硕、语言+学位衔接项目'
                    ]
                  },
                  {
                    icon: <ComputerIcon />,
                    title: '教育数字化 & SaaS 系统合作（未来增长）',
                    lines: [
                      '系统方向：留学申请流程系统、CRM 客户管理、项目管理系统、AI 顾问 / 文书 / 销售辅助',
                      '适合对象：留学机构、教育公司、项目方。帮助合作方降本、提效、标准化运营。'
                    ]
                  }
                ].map((cap, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={idx}>
                    <Card sx={{ height: '100%', p: 2, borderRadius: 2, boxShadow: 1 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                          <Avatar sx={{ bgcolor: 'secondary.main', width: 56, height: 56 }}>{cap.icon}</Avatar>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {cap.title}
                          </Typography>
                        </Box>
                        {cap.lines.map((line, i2) => (
                          <Typography key={i2} variant="body2" sx={{ color: i2 === cap.lines.length - 1 ? 'text.secondary' : '#333', mb: 0.5 }}>
                            {line}
                          </Typography>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            <Paper sx={{ p: 4, borderRadius: 3, mb: 4 }}>
              <Typography variant="h5" sx={{ color: '#052391', fontWeight: 700, mb: 2 }}>
                三、为什么选择希航教育？
              </Typography>
              <Grid container spacing={2}>
                {[
                  '10 年马来西亚留学实操经验',
                  '中国 + 马来西亚双实体公司',
                  '公立 / 私立 / 国际体系全覆盖',
                  '不仅做申请，更能完成交付',
                  '项目制、批量化、长期合作经验成熟'
                ].map((t, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Card sx={{ p: 2, height: '100%' }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>{i + 1}</Avatar>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {t}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            <Paper sx={{ p: 4, borderRadius: 3, mb: 4 }}>
              <Typography variant="h5" sx={{ color: '#052391', fontWeight: 700, mb: 2 }}>
                四、合作理念
              </Typography>
              <Typography variant="body1" sx={{ color: '#333', mb: 2 }}>
                不是简单“资源对接”，而是长期、稳定、可复制的项目合作。
              </Typography>
              <List>
                <ListItem>
                  <ListItemText primary="方案可落地" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="过程可监管" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="结果可交付" />
                </ListItem>
              </List>
            </Paper>

            <Paper sx={{ p: 4, borderRadius: 3, mb: 4 }}>
              <Typography variant="h5" sx={{ color: '#052391', fontWeight: 700, mb: 2 }}>
                五、合作方式
              </Typography>
              <Typography variant="body1" sx={{ color: '#333', mb: 2 }}>
                如果你是：留学机构、学校 / 教育集团、项目负责人、渠道或资源方，欢迎与我们沟通具体合作方向，
                我们可按你的需求，单独定制合作方案。
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button component={Link} to="/contact" variant="contained" color="primary">联系我们</Button>
                <Button variant="outlined" color="primary" onClick={openWeChatQR}>索取合作手册</Button>
              </Stack>
            </Paper>
            
            <WeChatQRDialog open={openQR} onClose={closeWeChatQR} title="索取合作手册" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default BusinessCooperation;
